import React from 'react';
import { AlertTriangle, RefreshCcw, Home as HomeIcon } from 'lucide-react';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

// Fire-and-forget report to backend so we can diagnose crashes even when
// the user only tells us "something went wrong". Uses navigator.sendBeacon
// when available (survives page unloads); falls back to fetch keepalive.
function reportToBackend(payload) {
  try {
    const url = `${BACKEND}/api/errors/report`;
    const body = JSON.stringify(payload);
    if (navigator?.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
      return;
    }
    fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
  } catch { /* never let the boundary itself throw */ }
}

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, componentStack: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('ErrorBoundary caught:', error, info);

    // Stale tabs after a redeploy try to fetch a JS chunk whose hashed
    // filename no longer exists → ChunkLoadError. Auto-reload once to
    // recover; guard with sessionStorage so a persistent failure doesn't
    // loop reloads forever.
    const isChunkLoadError = error?.name === 'ChunkLoadError'
      || /Loading chunk [\w.-]+ failed/i.test(error?.message || '');
    if (isChunkLoadError) {
      const RELOAD_FLAG = 'habita_chunk_reload_attempted';
      let alreadyReloaded = false;
      try { alreadyReloaded = sessionStorage.getItem(RELOAD_FLAG) === '1'; } catch { /* storage unavailable */ }
      if (!alreadyReloaded) {
        try { sessionStorage.setItem(RELOAD_FLAG, '1'); } catch { /* storage unavailable */ }
        window.location.reload();
        return;
      }
    }

    if (window.posthog) {
      try { window.posthog.capture('frontend_error', { message: error?.message, stack: error?.stack?.slice(0, 2000) }); } catch { /* posthog optional */ }
    }
    reportToBackend({
      message: error?.message || 'unknown',
      name: error?.name || 'Error',
      stack: (error?.stack || '').slice(0, 4000),
      component_stack: (info?.componentStack || '').slice(0, 2000),
      url: window.location?.href || '',
      user_agent: navigator?.userAgent || '',
      at: new Date().toISOString(),
    });
    this.setState({ componentStack: info?.componentStack || null });
  }

  reset = () => {
    // Reset local error state so the tree re-mounts. Only useful when the
    // user has fixed the state that caused the crash. If reset triggers the
    // same crash again the boundary will simply re-open.
    this.setState({ hasError: false, error: null, componentStack: null });
  };

  render() {
    if (this.state.hasError) {
      const message = this.state.error?.message || 'Error desconocido';
      // A short, single-line, safe-to-show excerpt. We never surface raw
      // stacks to end users but the message helps them tell us what happened.
      const shortMessage = message.length > 220 ? `${message.slice(0, 220)}…` : message;
      return (
        <div className="min-h-screen bg-habita-cream flex items-center justify-center p-6" data-testid="error-boundary">
          <div className="max-w-md text-center bg-white rounded-3xl p-10 border border-gray-200 shadow-sm">
            <div className="w-16 h-16 rounded-2xl bg-rose-100 mx-auto flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-rose-600" />
            </div>
            <h1 className="font-serif text-3xl font-bold text-habita-dark mt-5">Se produjo un error inesperado</h1>
            <p className="text-gray-600 mt-3">
              Nuestro equipo fue notificado automáticamente. Puedes reintentar o volver al inicio.
            </p>
            <div className="mt-4 text-left bg-gray-50 border border-gray-200 rounded-xl p-3">
              <p className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">Detalles técnicos</p>
              <p className="text-xs text-gray-800 font-mono mt-1 break-words" data-testid="error-boundary-message">{shortMessage}</p>
            </div>
            <div className="mt-6 flex gap-3 justify-center flex-wrap">
              <button
                onClick={this.reset}
                className="btn-habita inline-flex items-center gap-2 px-6 h-11 rounded-full font-semibold"
                data-testid="error-boundary-retry"
              >
                <RefreshCcw className="w-4 h-4" /> Reintentar
              </button>
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center gap-2 px-6 h-11 rounded-full font-semibold border border-gray-200 text-gray-700 hover:bg-gray-50"
                data-testid="error-boundary-reload"
              >
                <RefreshCcw className="w-4 h-4" /> Recargar página
              </button>
              <a href="/" className="inline-flex items-center gap-2 px-6 h-11 rounded-full font-semibold border border-gray-200 text-gray-700 hover:bg-gray-50">
                <HomeIcon className="w-4 h-4" /> Inicio
              </a>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
